import { useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  ArrowLeft,
  ChevronRight,
  Download,
  Loader2,
  MessageCircle,
  Pencil,
  Copy,
  Send,
  Check,
  X,
  User,
  Calendar,
  FileText,
} from 'lucide-react'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import type { Presupuesto, PresupuestoItem, EstadoPresupuesto } from '@/types/database'
import { PresupuestoTemplate } from '@/components/presupuestos/PresupuestoTemplate'
import { EnviarPresupuestoDialog } from '@/components/presupuestos/EnviarPresupuestoDialog'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import {
  ConfiguracionEstudioNoDisponibleError,
  exigirConfiguracionEstudio,
} from '@/services/configuracionService'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { formatCurrency, formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import {
  usePresupuesto,
  useCambiarEstadoPresupuesto,
  useDuplicarPresupuesto,
  useNombreArchivoPresupuesto,
} from '@/hooks/usePresupuestos'
import { useServicios } from '@/hooks/useServicios'
import { usePermissions } from '@/hooks/usePermissions'

function EstadoBadge({ estado }: { estado: EstadoPresupuesto }) {
  const variants: Record<EstadoPresupuesto, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    borrador: { variant: 'secondary', label: 'Borrador' },
    enviado: { variant: 'outline', label: 'Enviado' },
    aceptado: { variant: 'default', label: 'Aceptado' },
    rechazado: { variant: 'destructive', label: 'Rechazado' },
  }

  const config = variants[estado] ?? { variant: 'secondary', label: estado }
  return <Badge variant={config.variant}>{config.label}</Badge>
}

// Helper to safely parse items from presupuesto
function parseItems(presupuesto: Presupuesto): PresupuestoItem[] {
  if (!presupuesto.items) {
    return []
  }

  // Items might be a JSON string or already parsed
  if (typeof presupuesto.items === 'string') {
    try {
      return JSON.parse(presupuesto.items) as PresupuestoItem[]
    } catch {
      return []
    }
  }

  // Items is Json type from Supabase, cast through unknown
  return presupuesto.items as unknown as PresupuestoItem[]
}

export function PresupuestoDetalle() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const templateRef = useRef<HTMLDivElement>(null)

  const [generandoPDF, setGenerandoPDF] = useState(false)
  const [compartiendo, setCompartiendo] = useState(false)
  const [presupuestoEnviar, setPresupuestoEnviar] = useState<Presupuesto | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  // Queries
  const { data: presupuesto, isLoading, error } = usePresupuesto(id ?? '')
  const { data: configEstudio, isError: configEstudioError } = useConfiguracionEstudio()
  const { organizacion } = useOrganizacion()
  const { data: servicios = [] } = useServicios()
  const { puede } = usePermissions()

  // ORR-120: nombre del archivo `Presupuesto-{Apellido}.pdf` (descarga + WhatsApp + adjunto mail)
  const nombreArchivo = useNombreArchivoPresupuesto(presupuesto)

  // Mutations
  const cambiarEstado = useCambiarEstadoPresupuesto()
  const duplicarPresupuesto = useDuplicarPresupuesto()

  const handleVolver = () => {
    navigate('/presupuestos')
  }

  const handleEditar = () => {
    if (presupuesto) {
      navigate(`/presupuestos/${presupuesto.id}/editar`)
    }
  }

  const handleDuplicar = async () => {
    if (!presupuesto) return
    try {
      await duplicarPresupuesto.mutateAsync(presupuesto.id)
      toast.success('Presupuesto duplicado correctamente')
      navigate('/presupuestos')
    } catch (err) {
      toast.error('Error al duplicar el presupuesto')
      console.error(err)
    }
  }

  const handleCambiarEstado = async (nuevoEstado: EstadoPresupuesto) => {
    if (!presupuesto) return
    try {
      await cambiarEstado.mutateAsync({ id: presupuesto.id, estado: nuevoEstado })
      toast.success(`Presupuesto marcado como ${nuevoEstado}`)
    } catch (err) {
      toast.error('Error al cambiar el estado')
      console.error(err)
    }
  }


  const generarPdf = async () => {
    if (!presupuesto || !templateRef.current) return null
    exigirConfiguracionEstudio({ data: configEstudio, isError: configEstudioError })

    const canvas = await html2canvas(templateRef.current, {
      scale: 1.6,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 794,
      height: 1123,
    })

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    })

    const pageWidth = 210
    const pageHeight = 297
    const imgData = canvas.toDataURL('image/jpeg', 0.92)
    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST')

    return pdf
  }

  const handleCompartirWhatsApp = async () => {
    if (!presupuesto) return
    const numero = (presupuesto.numero_presupuesto || 0).toString().padStart(4, '0')
    const fechaValidez = new Date(presupuesto.fecha || new Date())
    fechaValidez.setDate(fechaValidez.getDate() + (presupuesto.validez_dias ?? 0))

    const mensaje = `*Presupuesto #${numero}*
Cliente: ${presupuesto.cliente_nombre || 'Sin nombre'}
Total: ${formatCurrency(presupuesto.total || 0)}
Válido hasta: ${formatDate(fechaValidez.toISOString().split('T')[0])}`

    const fallbackUrl = `https://wa.me/?text=${encodeURIComponent(mensaje)}`
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void>; canShare?: (data: ShareData) => boolean }

    setCompartiendo(true)
    try {
      const pdf = await generarPdf()
      if (pdf && nav.share && nav.canShare) {
        const blob = pdf.output('blob')
        const file = new File([blob], nombreArchivo, { type: 'application/pdf' })
        if (nav.canShare({ files: [file], text: mensaje })) {
          await nav.share({ files: [file], text: mensaje })
          toast.success('Presupuesto listo para compartir')
          return
        }
      }

      window.open(fallbackUrl, '_blank')
      toast.info('Tu navegador no permite adjuntar el archivo; se envía solo el texto.')
    } catch (error) {
      console.error('Error compartiendo por WhatsApp:', error)
      window.open(fallbackUrl, '_blank')
      toast.info('No se pudo adjuntar el PDF; se envía solo el texto.')
    } finally {
      setCompartiendo(false)
    }
  }

  const handleExportarPDF = async () => {
    if (!presupuesto) return

    setGenerandoPDF(true)
    try {
      const pdf = await generarPdf()
      if (!pdf) {
        toast.error('No se pudo generar el PDF')
        return
      }
      pdf.save(nombreArchivo)
      toast.success('PDF generado correctamente')
    } catch (error) {
      console.error('Error generando PDF:', error)
      toast.error(
        error instanceof ConfiguracionEstudioNoDisponibleError
          ? error.message
          : 'Error al generar el PDF',
      )
    } finally {
      setGenerandoPDF(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Presupuesto" description="Cargando..." />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
      </div>
    )
  }

  // Error state
  if (error || !presupuesto) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Presupuesto" description="Error al cargar" />
        <main className="flex-1 p-6">
          <div className="mb-6">
            <Button variant="ghost" size="sm" onClick={handleVolver} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Volver a Presupuestos
            </Button>
          </div>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">
              No se pudo cargar el presupuesto. Es posible que no exista o no tengas permisos para verlo.
            </p>
          </div>
        </main>
      </div>
    )
  }

  const items = parseItems(presupuesto)
  const estado = (presupuesto.estado as EstadoPresupuesto) ?? 'borrador'

  const fechaValidez = new Date(presupuesto.fecha || new Date())
  fechaValidez.setDate(fechaValidez.getDate() + (presupuesto.validez_dias ?? 0))

  const subtotalItems = items.reduce((acc, item) => acc + (item?.subtotal ?? 0), 0)
  const descuentoPorcentaje = presupuesto.descuento_porcentaje ?? 0
  const ivaPorcentaje = presupuesto.iva_porcentaje ?? 0
  const descuento = subtotalItems * (descuentoPorcentaje / 100)
  const baseImponible = subtotalItems - descuento
  const iva = baseImponible * (ivaPorcentaje / 100)

  const numeroFormateado = (presupuesto.numero_presupuesto || 0).toString().padStart(4, '0')
  const hayDescuentoEnItems = items.some(item => (item.descuento_porcentaje ?? 0) > 0)

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={`Presupuesto #${numeroFormateado}`}
        description={`Cliente: ${presupuesto.cliente_nombre}`}
      />

      <main className="flex-1 p-6 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleVolver} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Volver a Presupuestos
          </Button>
          <EstadoBadge estado={estado} />
          <Badge variant="outline" className="text-xs">
            Valido hasta: {formatDate(fechaValidez.toISOString().split('T')[0])}
          </Badge>
        </div>

        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Presupuesto</p>
                  <p className="text-2xl font-semibold text-foreground">#{numeroFormateado}</p>
                  <p className="text-sm text-muted-foreground">Emitido el {formatDate(presupuesto.fecha || new Date().toISOString())}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportarPDF}
                  disabled={generandoPDF}
                  className="gap-2"
                >
                  {generandoPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCompartirWhatsApp}
                  disabled={compartiendo}
                  className="gap-2 text-green-700 hover:text-green-800 hover:bg-green-50"
                >
                  {compartiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                  {compartiendo ? 'Compartiendo...' : 'WhatsApp'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleEditar} className="gap-2">
                  <Pencil className="h-4 w-4" />
                  Editar
                </Button>
                <Button variant="outline" size="sm" onClick={handleDuplicar} className="gap-2">
                  <Copy className="h-4 w-4" />
                  Duplicar
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border bg-muted/30 px-4 py-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cliente</p>
                <div className="mt-2 flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-2 text-primary">
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{presupuesto.cliente_nombre}</p>
                    {presupuesto.cliente_cuit && (
                      <p className="text-sm text-muted-foreground">CUIT: {presupuesto.cliente_cuit}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 px-4 py-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Validez</p>
                <div className="mt-2 flex items-start gap-3">
                  <div className="rounded-full bg-white text-muted-foreground p-2 shadow-sm">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      Hasta {formatDate(fechaValidez.toISOString().split('T')[0])}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {presupuesto.validez_dias ?? 15} dias desde la emision
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-primary/5 px-4 py-3">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider">Total</p>
                <p className="mt-2 text-3xl font-bold text-primary">{formatCurrency(presupuesto.total || 0)}</p>
                <p className="text-sm text-muted-foreground">
                  Subtotal {formatCurrency(presupuesto.subtotal ?? 0)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* ORR-200 B8: acciones de negocio gateadas por flag fino */}
              {estado === 'borrador' && puede.enviarPresupuesto && (
                <Button variant="default" size="sm" onClick={() => setPresupuestoEnviar(presupuesto)} className="gap-2">
                  <Send className="h-4 w-4" />
                  Enviar por email
                </Button>
              )}

              {(estado === 'borrador' || estado === 'enviado') && puede.aceptarPresupuesto && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleCambiarEstado('aceptado')}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4" />
                  Aceptar
                </Button>
              )}
              {(estado === 'borrador' || estado === 'enviado') && puede.rechazarPresupuesto && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCambiarEstado('rechazado')}
                  className="gap-2 text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                  Rechazar
                </Button>
              )}

            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <div className="space-y-6">
            <Card className="border-dashed">
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/40">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-semibold text-foreground">Detalle de Servicios</h3>
                  </div>
                  <span className="text-xs text-muted-foreground">{items.length} concepto(s)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30 text-left text-xs font-semibold text-muted-foreground uppercase">
                        <th className="px-6 py-3">Servicio</th>
                        <th className="px-4 py-3 text-center">Cant.</th>
                        {hayDescuentoEnItems && <th className="px-4 py-3 text-center">Dto.%</th>}
                        <th className="px-4 py-3 text-right">Precio Unit.</th>
                        <th className="px-6 py-3 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {items.map((item, index) => {
                        const servicio = servicios.find(s => s.id === item.servicio_id)
                        const descripcionTexto = item.descripcion ?? servicio?.descripcion
                        const detalleLines = descripcionTexto?.split('\n').filter(line => line.trim()) || []
                        const hasDetail = detalleLines.length > 0
                        const isExpanded = expandedRows.has(index)

                        const toggleRow = () => {
                          setExpandedRows(prev => {
                            const next = new Set(prev)
                            if (next.has(index)) next.delete(index)
                            else next.add(index)
                            return next
                          })
                        }

                        return (
                          <tr
                            key={index}
                            className={`hover:bg-muted/30 transition-colors ${hasDetail ? 'cursor-pointer' : ''}`}
                            onClick={hasDetail ? toggleRow : undefined}
                            role={hasDetail ? 'button' : undefined}
                            tabIndex={hasDetail ? 0 : undefined}
                            aria-expanded={hasDetail ? isExpanded : undefined}
                            onKeyDown={
                              hasDetail
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault()
                                      toggleRow()
                                    }
                                  }
                                : undefined
                            }
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-start gap-2">
                                {hasDetail && (
                                  <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                )}
                                <div>
                                  <span className="font-medium text-foreground">{servicio?.nombre ?? 'Servicio no encontrado'}</span>
                                  {isExpanded && (
                                    <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground list-disc ml-4">
                                      {detalleLines.map((line, i) => (
                                        <li key={i}>{line}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center text-muted-foreground align-top">{item.cantidad ?? 0}</td>
                            {hayDescuentoEnItems && (
                              <td className="px-4 py-4 text-center align-top">
                                {(item.descuento_porcentaje ?? 0) > 0 ? (
                                  <span className="text-sm font-medium text-green-600">{item.descuento_porcentaje}%</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-4 text-right text-muted-foreground align-top">
                              {formatCurrency(item.precio_unitario ?? 0)}
                            </td>
                            <td className="px-6 py-4 text-right font-semibold align-top">{formatCurrency(item.subtotal ?? 0)}</td>
                          </tr>
                        )
                      })}
                      {items.length === 0 && (
                        <tr>
                          <td colSpan={hayDescuentoEnItems ? 5 : 4} className="px-6 py-5 text-center text-muted-foreground">
                            No hay items en este presupuesto
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {presupuesto.notas && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Observaciones</h3>
                  </div>
                  <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">{presupuesto.notas}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <Card>
              <CardContent className="p-5 space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Resumen</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">{formatCurrency(presupuesto.subtotal ?? 0)}</span>
                  </div>
                  {descuentoPorcentaje > 0 && (
                    <div className="flex justify-between text-sm text-green-700">
                      <span>Descuento ({descuentoPorcentaje}%)</span>
                      <span>-{formatCurrency(descuento)}</span>
                    </div>
                  )}
                  {ivaPorcentaje > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">IVA ({ivaPorcentaje}%)</span>
                      <span className="font-medium">{formatCurrency(iva)}</span>
                    </div>
                  )}
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-2xl font-bold text-primary">{formatCurrency(presupuesto.total || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Informacion</h3>
                <div className="space-y-2 text-sm">
                  {presupuesto.created_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Creado</span>
                      <span>{formatDate(presupuesto.created_at)}</span>
                    </div>
                  )}
                  {presupuesto.updated_at && presupuesto.updated_at !== presupuesto.created_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Modificado</span>
                      <span>{formatDate(presupuesto.updated_at)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Validez</span>
                    <span>{presupuesto.validez_dias ?? 15} dias</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estado actual</span>
                    <span className="font-medium capitalize">{estado}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Dialog para enviar presupuesto por email */}
      <EnviarPresupuestoDialog
        open={!!presupuestoEnviar}
        onOpenChange={(open) => !open && setPresupuestoEnviar(null)}
        presupuesto={presupuestoEnviar}
      />

      {/* Hidden template for PDF generation */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <PresupuestoTemplate
          ref={templateRef}
          presupuesto={presupuesto}
          servicios={servicios}
          configEstudio={configEstudio}
          organizacion={organizacion}
        />
      </div>
    </div>
  )
}
